import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';
import ReactModal from 'react-modal';

// components
import Table from '../Table';
import SearchIcon from '../../../assets/images/svg/search.svg';
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';

// Tools
import { dateFormat } from '../../tools/manager';

/** -------------------------------------------
 * Constants
 * ------------------------------------------ */
const TABS = {
    ORDER: 'ORDER',
    INTERVENTION: 'INTERVENTION',                 // ✅ BI non facturés (null + "0")
    INTERVENTION_INVOICED: 'INTERVENTION_INVOICED', // ✅ BI facturés ("1")
    PREFILL: 'PREFILL',
};

const INITIAL_TAB_STATE = {
    items: [],
    nextToken: null,
    loading: false,
    initialized: false,
};

const BASE_HEADERS = [
    'Réalisé le',
    'Fait par',
    'Numéro',
    'Type de chantier',
    'Nom du client',
    'Nom du chantier',
    'Statut',
    { label: 'Actions', className: 'text-right' },
];

const getValidTab = (value) =>
    Object.values(TABS).includes(value) ? value : TABS.ORDER;

/** -------------------------------------------
 * Helpers
 * ------------------------------------------ */
const buildRevisionMaps = ({ enabled, items }) => {
    if (!enabled) {
        return { enabled: false, newById: new Map(), oldByNewId: new Map() };
    }

    const newById = new Map();
    for (const c of items) {
        if (c?.id) newById.set(c.id, c);
    }

    const oldByNewId = new Map();
    for (const oldCoupon of items) {
        const newId = oldCoupon?.revisionOfId;
        if (newId) oldByNewId.set(newId, oldCoupon);
    }

    return { enabled: true, newById, oldByNewId };
};

const needsClassification = (coupon, worksiteKey) => {
    const worksite = coupon?.[worksiteKey] || {};
    const hasCompany = !!worksite?.company?.name;
    const hasWorksiteName = !!worksite?.name;
    return !(hasCompany && hasWorksiteName);
};

const formatSearchFields = (coupon) => {
    const date = coupon?.createdAt || coupon?.dateSigned;
    const formatted = date ? dateFormat(date) : null;

    const worksite = coupon?.worksite || coupon?.worksites || {};
    const fields = [
        worksite?.company?.name,
        worksite?.name,
        worksite?.type?.name,
        coupon?.numero,
        coupon?.collaborator?.first_name,
        coupon?.collaborator?.last_name,
    ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());

    if (formatted) fields.push(formatted.day, formatted.month, formatted.year);
    return fields;
};

const uniqById = (arr) => {
    const map = new Map();
    for (const x of arr || []) {
        if (x?.id) map.set(x.id, x);
    }
    return Array.from(map.values());
};

/** -------------------------------------------
 * UI small components
 * ------------------------------------------ */
function TabsBar({ tabs, activeTab, onChange }) {
    return (
        <div className="flex flex-wrap gap-3 mt-4 border-b border-gray-200">
            {tabs.map(({ key, label, badge }) => {
                const isActive = activeTab === key;

                return (
                    <button
                        key={key}
                        className={`pb-2 px-3 text-sm md:text-base flex items-center gap-2 ${isActive
                            ? 'border-b-2 border-primary-blue font-semibold text-primary-blue'
                            : 'text-gray-500'
                            }`}
                        onClick={() => onChange(key)}
                        type="button"
                    >
                        <span>{label}</span>

                        {!!badge && (
                            <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${isActive
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-600'
                                    }`}
                            >
                                {badge}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function SearchBar({ value, onChange, placeholder }) {
    return (
        <div className="relative flex mt-6 border border-gray-admin rounded-xl">
            <input
                type="text"
                placeholder={placeholder}
                className="w-full py-3 pl-10 border-none rounded-xl placeholder:text-blue-admin inputFocus"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />

            <img
                src={SearchIcon}
                alt="search icon"
                className="absolute left-0 flex items-center w-8 h-5 pl-3 top-4"
            />
        </div>
    );
}

function DeleteModal({ isOpen, onClose, onConfirm, coupon }) {
    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto outline-none mt-80"
            overlayClassName="fixed inset-0 bg-black/40 flex items-start justify-center z-50"
        >
            <h2 className="text-lg font-semibold mb-2">Confirmer la suppression</h2>

            <p className="text-sm text-gray-700 mb-6">
                Voulez-vous vraiment supprimer le bon n°{' '}
                <span className="font-semibold">{coupon?.numero || coupon?.id}</span> ?
            </p>

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg border text-sm"
                >
                    Annuler
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700"
                >
                    Supprimer
                </button>
            </div>
        </ReactModal>
    );
}

function TableHeaders({
    headers,
    showSelect,
    masterState, // 'none' | 'some' | 'all'
    onToggleAll,
}) {
    return (
        <thead>
            <tr>
                {showSelect && (
                    <th className="w-10">
                        <div className="flex items-center justify-center">
                            <input
                                type="checkbox"
                                checked={masterState === 'all'}
                                ref={(el) => {
                                    if (el) el.indeterminate = masterState === 'some';
                                }}
                                onChange={onToggleAll}
                            />
                        </div>
                    </th>
                )}

                {headers.map((header, index) => {
                    const label = typeof header === 'string' ? header : header.label;
                    const className = typeof header === 'string' ? '' : header.className;
                    return (
                        <th key={index} className={className}>
                            {label}
                        </th>
                    );
                })}
            </tr>
        </thead>
    );
}

function BulkActionBar({
    variant, // 'invoice' | 'uninvoice'
    selectedCount,
    totalVisible,
    onToggleAll,
    onClear,
    onPrimary,
    loading,
}) {
    const isInvoice = variant === 'invoice';

    return (
        <div className="mt-4 p-4 rounded-2xl border border-gray-200 bg-white shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
                <span className="text-sm font-semibold">
                    Sélection : {selectedCount} / {totalVisible}
                </span>

                <span
                    className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${isInvoice ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                        }`}
                >
                    {isInvoice ? 'À facturer' : 'Facturés'}
                </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 justify-end">
                <button
                    type="button"
                    className="px-4 py-2 rounded-lg border text-sm"
                    onClick={onToggleAll}
                    disabled={!totalVisible}
                >
                    Tout / Rien
                </button>

                <button
                    type="button"
                    className="px-4 py-2 rounded-lg border text-sm"
                    onClick={onClear}
                    disabled={!selectedCount}
                >
                    Réinitialiser
                </button>

                <button
                    type="button"
                    className={`px-4 py-2 rounded-lg text-sm text-white disabled:opacity-50 ${isInvoice ? 'bg-primary-blue hover:bg-blue-700' : 'bg-gray-900 hover:bg-black'
                        }`}
                    onClick={onPrimary}
                    disabled={!selectedCount || loading}
                >
                    {loading
                        ? 'Traitement...'
                        : isInvoice
                            ? `Facturer (${selectedCount})`
                            : `Défacturer (${selectedCount})`}
                </button>
            </div>
        </div>
    );
}

function ConfirmBulkModal({ isOpen, onClose, onConfirm, count, mode, loading }) {
    const isInvoice = mode === 'invoice';

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto outline-none mt-52"
            overlayClassName="fixed inset-0 bg-black/40 flex items-start justify-center z-50"
        >
            <h2 className="text-lg font-semibold mb-2">
                {isInvoice ? 'Confirmer la facturation' : 'Confirmer la défacturation'}
            </h2>

            <p className="text-sm text-gray-700 mb-6">
                Vous allez {isInvoice ? 'marquer comme facturé' : 'repasser en non facturé'}{' '}
                <span className="font-semibold">{count}</span> bon(s).
                <br />
                Cette action met à jour le statut et déplace les bons dans l’onglet correspondant.
            </p>

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg border text-sm"
                >
                    Annuler
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={!count || loading}
                    className={`px-4 py-2 rounded-lg text-sm text-white disabled:opacity-50 ${isInvoice ? 'bg-primary-blue hover:bg-blue-700' : 'bg-gray-900 hover:bg-black'
                        }`}
                >
                    {loading ? 'Traitement...' : 'Confirmer'}
                </button>
            </div>
        </ReactModal>
    );
}

/** -------------------------------------------
 * Main
 * ------------------------------------------ */
const Coupons = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    /** ---------------------------
     * Easy-Peasy actions
     * -------------------------- */
    const listAllOrdersCoupons = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.listAllOrdersCoupons
    );
    // const listAllInterventionCoupons = useStoreActions(
    //     (actions) => actions.middleware.OrdersMiddleware.listAllInterventionCoupons
    // );
    const listAllPrefillCoupons = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.listAllPrefillCoupons
    );

    // ✅ nouveaux middlewares
    const listInterventionCouponsInvoicedTrue = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.listInterventionCouponsInvoicedTrue
    );
    const listInterventionCouponsInvoicedFalseOrNull = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.listInterventionCouponsInvoicedFalseOrNull
    );

    // ✅ bulk update
    const bulkUpdateInterventionCouponsInvoiced = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.bulkUpdateInterventionCouponsInvoiced
    );

    const deleteOrderCoupon = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.removeOrderCoupon
    );
    const deleteInterventionCoupon = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.removeInterventionCoupon
    );
    const deletePrefillCoupon = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.removePrefillCoupon
    );

    const getUser = useStoreActions(
        (actions) => actions.middleware.AdminMiddleware.getAdmin
    );

    /** ---------------------------
     * Tab config (memoized)
     * -------------------------- */
    const TAB_CONFIG = useMemo(
        () => ({
            [TABS.ORDER]: {
                key: TABS.ORDER,
                label: 'Bons de commande',
                placeholder: 'Recherche bon de commande',
                list: listAllOrdersCoupons,
                remove: deleteOrderCoupon,
                worksiteKey: 'worksite',
                route: (id) => `/pdf-preview/order/${id}`,
                enrich: async (items) =>
                    Promise.all(
                        items.map(async (el) => {
                            if (!el?.collaborator && el?.orderCouponCollaboratorId) {
                                const user = await getUser(el.orderCouponCollaboratorId);
                                return {
                                    ...el,
                                    collaborator: {
                                        first_name: user?.first_name || '',
                                        last_name: user?.last_name || '',
                                    },
                                };
                            }
                            return el;
                        })
                    ),
            },

            // ✅ Non facturés (inclut null au début)
            [TABS.INTERVENTION]: {
                key: TABS.INTERVENTION,
                label: "Bons d'intervention à facturer",
                placeholder: 'Recherche bon d’intervention',
                list: listInterventionCouponsInvoicedFalseOrNull,
                remove: deleteInterventionCoupon,
                worksiteKey: 'worksite',
                route: (id) => `/pdf-preview/intervention/${id}`,
            },

            // ✅ Facturés
            [TABS.INTERVENTION_INVOICED]: {
                key: TABS.INTERVENTION_INVOICED,
                label: "Bons d'intervention facturés",
                placeholder: 'Recherche bon d’intervention facturé',
                list: listInterventionCouponsInvoicedTrue,
                remove: deleteInterventionCoupon,
                worksiteKey: 'worksite',
                route: (id) => `/pdf-preview/intervention/${id}`,
            },

            [TABS.PREFILL]: {
                key: TABS.PREFILL,
                label: 'Bons pré-remplis',
                placeholder: 'Recherche bon pré-rempli',
                list: listAllPrefillCoupons,
                remove: deletePrefillCoupon,
                worksiteKey: 'worksites',
                route: (id) => `/pdf-preview/Pre-intervention/${id}`,
            },
        }),
        [
            listAllOrdersCoupons,
            listAllPrefillCoupons,
            deleteOrderCoupon,
            deleteInterventionCoupon,
            deletePrefillCoupon,
            getUser,

            listInterventionCouponsInvoicedTrue,
            listInterventionCouponsInvoicedFalseOrNull,
        ]
    );

    /** ---------------------------
     * State
     * -------------------------- */
    const [activeTab, setActiveTab] = useState(() =>
        getValidTab(searchParams.get('tab'))
    );
    const [search, setSearch] = useState('');

    const [stateByTab, setStateByTab] = useState({
        [TABS.ORDER]: { ...INITIAL_TAB_STATE },
        [TABS.INTERVENTION]: { ...INITIAL_TAB_STATE },
        [TABS.INTERVENTION_INVOICED]: { ...INITIAL_TAB_STATE },
        [TABS.PREFILL]: { ...INITIAL_TAB_STATE },
    });

    const currentConfig = TAB_CONFIG[activeTab];
    const currentState = stateByTab[activeTab];

    const updateTabState = useCallback((tabKey, patch) => {
        setStateByTab((prev) => ({
            ...prev,
            [tabKey]: {
                ...prev[tabKey],
                ...(typeof patch === 'function' ? patch(prev[tabKey]) : patch),
            },
        }));
    }, []);

    const updateCurrentTabState = useCallback(
        (patch) => updateTabState(activeTab, patch),
        [activeTab, updateTabState]
    );

    // ✅ sélection sur BI non facturés + BI facturés
    const isSelectableTab =
        activeTab === TABS.INTERVENTION || activeTab === TABS.INTERVENTION_INVOICED;

    const bulkMode = activeTab === TABS.INTERVENTION_INVOICED ? 'uninvoice' : 'invoice';
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    // reset selection when leaving tab or when data changes hard
    useEffect(() => {
        setSelectedIds(new Set());
    }, [activeTab]);

    /** ---------------------------
     * Delete modal
     * -------------------------- */
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);

    const openDeleteModal = useCallback((coupon) => {
        setCouponToDelete(coupon);
        setIsDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        setIsDeleteModalOpen(false);
        setCouponToDelete(null);
    }, []);

    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);

    const selectedCount = selectedIds.size; // ✅ remonter ici (avant openBulkModal)

    const openBulkModal = useCallback(() => {
        if (!selectedIds.size) return; // ✅ pas besoin de selectedCount
        setIsBulkModalOpen(true);
    }, [selectedIds]);

    const closeBulkModal = useCallback(() => setIsBulkModalOpen(false), []);

    /** ---------------------------
     * URL sync
     * -------------------------- */
    useEffect(() => {
        const urlTab = searchParams.get('tab');
        const validTab = getValidTab(urlTab);
        if (validTab !== activeTab) setActiveTab(validTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    /** ---------------------------
     * Load more (pagination)
     * -------------------------- */
    const handleLoadMore = useCallback(async () => {
        if (currentState.loading) return;

        const limit = 25;
        const { list, enrich } = currentConfig;

        updateCurrentTabState({ loading: true });

        try {
            const res = await list({
                limit,
                nextToken: currentState.nextToken,
            });

            let items = res.items || [];
            if (enrich) items = await enrich(items);

            updateCurrentTabState((prev) => ({
                items: uniqById([...prev.items, ...items]),
                nextToken: res.nextToken,
                loading: false,
                initialized: true,
            }));
        } catch (err) {
            console.error(err);
            toast.error('❗️ Une erreur est survenue lors du chargement de la liste des bons');
            updateCurrentTabState({ loading: false, initialized: true });
        }
    }, [currentState.loading, currentState.nextToken, currentConfig, updateCurrentTabState]);

    useEffect(() => {
        if (!currentState.initialized) handleLoadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    /** ---------------------------
     * Delete confirmed
     * -------------------------- */
    const confirmDeleteCoupon = useCallback(async () => {
        if (!couponToDelete) return;

        try {
            await currentConfig.remove(couponToDelete.id);

            updateCurrentTabState((prev) => ({
                items: prev.items.filter((item) => item.id !== couponToDelete.id),
            }));

            toast.success('✅ Bon supprimé avec succès');
        } catch (err) {
            console.error(err);
            toast.error('❗️ Erreur lors de la suppression du bon');
        } finally {
            closeDeleteModal();
        }
    }, [couponToDelete, currentConfig, updateCurrentTabState, closeDeleteModal]);

    /** ---------------------------
     * Revision maps (ORDER + INTERVENTION + INTERVENTION_INVOICED)
     * -------------------------- */
    const revisionMaps = useMemo(() => {
        const enabled =
            activeTab === TABS.ORDER ||
            activeTab === TABS.INTERVENTION ||
            activeTab === TABS.INTERVENTION_INVOICED;

        return buildRevisionMaps({ enabled, items: currentState?.items || [] });
    }, [activeTab, currentState?.items]);

    /** ---------------------------
     * Filtered items
     * -------------------------- */
    const filteredItems = useMemo(() => {
        const items = currentState?.items || [];
        if (!search.trim()) return items;

        const lower = search.toLowerCase();
        return items.filter((coupon) => {
            const fields = formatSearchFields(coupon);
            return fields.some((f) => f.includes(lower));
        });
    }, [search, currentState?.items]);

    /** ---------------------------
     * Selection helpers
     * -------------------------- */
    const toggleSelected = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const filteredIds = useMemo(
        () => (filteredItems || []).map((x) => x?.id).filter(Boolean),
        [filteredItems]
    );

    const masterState = useMemo(() => {
        if (!isSelectableTab || filteredIds.length === 0) return 'none';
        let count = 0;
        for (const id of filteredIds) if (selectedIds.has(id)) count++;
        if (count === 0) return 'none';
        if (count === filteredIds.length) return 'all';
        return 'some';
    }, [isSelectableTab, filteredIds, selectedIds]);

    const toggleAllFiltered = useCallback(() => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const allSelected = filteredIds.every((id) => next.has(id));

            if (allSelected) {
                // unselect all filtered
                for (const id of filteredIds) next.delete(id);
            } else {
                // select all filtered
                for (const id of filteredIds) next.add(id);
            }

            return next;
        });
    }, [filteredIds]);

    /** ---------------------------
     * Bulk invoice action
     * -------------------------- */
    const runBulkUpdate = useCallback(async () => {
        if (!selectedCount) return;

        const invoiced = bulkMode === 'invoice';

        try {
            setBulkLoading(true);

            const ids = Array.from(selectedIds);
            const itemsToUpdate = currentState.items
                .filter((x) => selectedIds.has(x.id) && x.createdAt)
                .map((x) => ({ id: x.id, createdAt: x.createdAt }));

            await bulkUpdateInterventionCouponsInvoiced({
                items: itemsToUpdate,
                invoiced,
                concurrency: 5,
            });

            // retirer immédiatement de l’onglet courant
            updateTabState(activeTab, (prev) => ({
                ...prev,
                items: prev.items.filter((x) => !selectedIds.has(x.id)),
            }));

            // reset l’autre onglet pour reload
            updateTabState(
                invoiced ? TABS.INTERVENTION_INVOICED : TABS.INTERVENTION,
                { ...INITIAL_TAB_STATE }
            );
            updateTabState(
                invoiced ? TABS.INTERVENTION : TABS.INTERVENTION_INVOICED,
                { ...INITIAL_TAB_STATE }
            );

            clearSelection();
            closeBulkModal();

            toast.success(
                invoiced
                    ? `✅ ${ids.length} bon(s) facturé(s)`
                    : `✅ ${ids.length} bon(s) repassé(s) en non facturé(s)`
            );
        } catch (err) {
            console.error(err);
            toast.error("❗️ Erreur lors de la mise à jour des bons");
        } finally {
            setBulkLoading(false);
        }
    }, [
        selectedCount,
        bulkMode,
        selectedIds,
        currentState.items,
        bulkUpdateInterventionCouponsInvoiced,
        updateTabState,
        activeTab,
        clearSelection,
        closeBulkModal,
    ]);

    /** ---------------------------
     * Navigation
     * -------------------------- */
    const handleRowClick = useCallback((coupon) => {
        if (coupon?.ctm === 'true') return;
        navigate(currentConfig.route(coupon.id));
    }, [navigate, currentConfig]);

    const tabsForUI = useMemo(
        () => [
            { key: TABS.ORDER, label: TAB_CONFIG[TABS.ORDER].label },
            { key: TABS.INTERVENTION, label: TAB_CONFIG[TABS.INTERVENTION].label, badge: 'À traiter' },
            { key: TABS.INTERVENTION_INVOICED, label: TAB_CONFIG[TABS.INTERVENTION_INVOICED].label, badge: 'Archivés' },
            { key: TABS.PREFILL, label: TAB_CONFIG[TABS.PREFILL].label },
        ],
        [TAB_CONFIG]
    );

    // ✅ Headers dynamiques (checkbox column uniquement sur non facturés)
    const headers = useMemo(() => {
        if (!isSelectableTab) return BASE_HEADERS;
        return [{ label: '', className: 'w-10' }, ...BASE_HEADERS];
    }, [isSelectableTab]);

    /** ---------------------------
     * Render
     * -------------------------- */
    return (
        <section className="md:pt-8 px-4 md:px-9">
            <TabsBar
                tabs={tabsForUI}
                activeTab={activeTab}
                onChange={(tabKey) => {
                    setActiveTab(tabKey);
                    setSearchParams({ tab: tabKey });
                }}
            />

            <SearchBar
                value={search}
                onChange={setSearch}
                placeholder={currentConfig.placeholder}
            />

            {/* ✅ Action bar sélection (BI non facturés + BI facturés) */}
            {isSelectableTab && (
                <BulkActionBar
                    variant={bulkMode === 'invoice' ? 'invoice' : 'uninvoice'}
                    selectedCount={selectedCount}
                    totalVisible={filteredItems.length}
                    onToggleAll={toggleAllFiltered}
                    onClear={clearSelection}
                    onPrimary={openBulkModal}
                    loading={bulkLoading}
                />
            )}

            <div className="mt-9">
                <Table>
                    <TableHeaders
                        headers={BASE_HEADERS}
                        showSelect={isSelectableTab}
                        masterState={masterState}
                        onToggleAll={toggleAllFiltered}
                    />
                    <tbody>
                        {filteredItems?.length ? (
                            filteredItems
                                .slice()
                                .sort((a, b) => (b.numero || 0) - (a.numero || 0))
                                .map((coupon) => {
                                    const dateValue = coupon?.createdAt || coupon?.dateSigned;
                                    const date = dateValue ? dateFormat(dateValue) : null;

                                    const worksite = coupon?.[currentConfig.worksiteKey] || {};
                                    const typeName = worksite?.type?.name || '-';
                                    const companyName =
                                        worksite?.company?.name || coupon?.nameClient || '-';
                                    const worksiteName =
                                        worksite?.name || coupon?.nameWorksite || '-';

                                    const fullName = coupon?.collaborator
                                        ? `${coupon.collaborator.first_name || ''} ${coupon.collaborator.last_name || ''
                                            }`.trim()
                                        : '';

                                    const createdBy = coupon?.name;
                                    const toClassify = needsClassification(
                                        coupon,
                                        currentConfig.worksiteKey
                                    );

                                    // Revision info
                                    let revisionInfo = null;
                                    if (revisionMaps.enabled) {
                                        if (coupon?.revisionOfId) {
                                            const newCoupon =
                                                revisionMaps.newById.get(coupon.revisionOfId) || null;
                                            revisionInfo = { kind: 'OLD', newCoupon };
                                        } else {
                                            const oldCoupon =
                                                revisionMaps.oldByNewId.get(coupon.id) || null;
                                            if (oldCoupon) revisionInfo = { kind: 'NEW', oldCoupon };
                                        }
                                    }

                                    const isSelected = selectedIds.has(coupon.id);

                                    return (
                                        <tr
                                            key={coupon.id}
                                            className={`h-16 border-b border-white-admin hover:bg-blue-50 transition-all ${coupon?.ctm !== 'true' ? 'cursor-pointer' : ''
                                                }`}
                                            onClick={() => handleRowClick(coupon)}
                                        >
                                            {/* ✅ checkbox column */}
                                            {isSelectableTab && (
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => toggleSelected(coupon.id)}
                                                    />
                                                </td>
                                            )}

                                            <td>
                                                {date ? `${date.day}.${date.month}.${date.year}` : '-'}
                                            </td>

                                            <td>{fullName || createdBy || '-'}</td>

                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="flex flex-col gap-1">
                                                    <div>
                                                        <span className="font-medium">
                                                            {coupon?.numero}{' '}
                                                            {coupon?.ctm === 'true' ? '- pré-rempli' : ''}
                                                        </span>
                                                    </div>

                                                    {revisionInfo?.kind === 'OLD' && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-orange-100 text-orange-700">
                                                                Modifié
                                                            </span>

                                                            <span className="text-xs text-gray-600">
                                                                → remplacé par{' '}
                                                                <span className="font-semibold">
                                                                    {revisionInfo?.newCoupon?.numero
                                                                        ? `N° ${revisionInfo.newCoupon.numero}`
                                                                        : 'un nouveau bon'}
                                                                </span>
                                                            </span>

                                                            {revisionInfo?.newCoupon?.id && (
                                                                <button
                                                                    type="button"
                                                                    className="text-xs text-blue-admin underline"
                                                                    onClick={() =>
                                                                        navigate(
                                                                            currentConfig.route(revisionInfo.newCoupon.id)
                                                                        )
                                                                    }
                                                                >
                                                                    Voir le nouveau
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}

                                                    {revisionInfo?.kind === 'NEW' && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-700">
                                                                Révision
                                                            </span>

                                                            <span className="text-xs text-gray-600">
                                                                remplace{' '}
                                                                <span className="font-semibold">
                                                                    {revisionInfo?.oldCoupon?.numero
                                                                        ? `N° ${revisionInfo.oldCoupon.numero}`
                                                                        : 'un ancien bon'}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            <td>{typeName || '-'}</td>
                                            <td>{companyName || '-'}</td>
                                            <td>{worksiteName || '-'}</td>

                                            <td onClick={(e) => e.stopPropagation()}>
                                                {toClassify ? (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                        À classer
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-50 text-green-600">
                                                        Classé
                                                    </span>
                                                )}
                                            </td>

                                            <td
                                                className="text-right"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div>
                                                    <button
                                                        onClick={() => openDeleteModal(coupon)}
                                                        className="w-8 p-2 border rounded-lg mt-7 border-white-admin"
                                                        type="button"
                                                    >
                                                        <img src={DeleteIcon} alt="delete icon" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                        ) : (
                            <tr>
                                <td
                                    className="text-center text-lg font-semibold pt-20"
                                    colSpan={isSelectableTab ? 9 : 8}
                                >
                                    {search
                                        ? "Oups, aucun bon ne correspond à votre recherche 🤭"
                                        : `Aucun ${currentConfig.label.toLowerCase()} pour le moment`}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {currentState.initialized &&
                    (currentState.loading || currentState.nextToken) && (
                        <div className="flex justify-center my-6">
                            <button
                                disabled={currentState.loading || !currentState.nextToken}
                                onClick={handleLoadMore}
                                className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                {currentState.loading
                                    ? 'Chargement...'
                                    : currentState.nextToken
                                        ? 'Charger plus'
                                        : 'Plus de résultats'}
                            </button>
                        </div>
                    )}
            </div>
            <ConfirmBulkModal
                isOpen={isBulkModalOpen}
                onClose={closeBulkModal}
                onConfirm={runBulkUpdate}
                count={selectedCount}
                mode={bulkMode === 'invoice' ? 'invoice' : 'uninvoice'}
                loading={bulkLoading}
            />

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDeleteCoupon}
                coupon={couponToDelete}
            />
        </section>
    );
};

export default Coupons;
// import React, { useEffect, useMemo, useState } from 'react';
// import { useNavigate, useSearchParams } from 'react-router-dom';
// import { useStoreActions } from 'easy-peasy';
// import { toast } from 'react-toastify';
// import ReactModal from 'react-modal';

// // components
// import Table from '../Table';
// import Search from '../../../assets/images/svg/search.svg';

// // icons
// import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';

// // Tools
// import { dateFormat } from '../../tools/manager';


// const TABS = {
//     ORDER: 'ORDER',
//     INTERVENTION: 'INTERVENTION',
//     PREFILL: 'PREFILL',
// };

// const INITIAL_TAB_STATE = {
//     items: [],
//     nextToken: null,
//     loading: false,
//     initialized: false,
// };

// const HEADERS = [
//     // 'Type',
//     'Réalisé le',
//     'Fait par',
//     'Numéro',
//     'Type de chantier',
//     'Nom du client',
//     'Nom du chantier',
//     'Statut',
//     { label: 'Actions', className: 'text-right' },
// ];


// const Coupons = () => {
//     const navigate = useNavigate();

//     // 🌱 Easy-Peasy actions
//     const listAllOrdersCoupons = useStoreActions(
//         (actions) => actions.middleware.OrdersMiddleware.listAllOrdersCoupons
//     );
//     const listAllInterventionCoupons = useStoreActions(
//         (actions) => actions.middleware.OrdersMiddleware.listAllInterventionCoupons
//     );
//     const listAllPrefillCoupons = useStoreActions(
//         (actions) => actions.middleware.OrdersMiddleware.listAllPrefillCoupons
//     );

//     const deleteOrderCoupon = useStoreActions(
//         (actions) => actions.middleware.OrdersMiddleware.removeOrderCoupon
//     );
//     const deleteInterventionCoupon = useStoreActions(
//         (actions) => actions.middleware.OrdersMiddleware.removeInterventionCoupon
//     );
//     const deletePrefillCoupon = useStoreActions(
//         (actions) => actions.middleware.OrdersMiddleware.removePrefillCoupon
//     );

//     const getUser = useStoreActions(
//         (actions) => actions.middleware.AdminMiddleware.getAdmin
//     );


//     // 🧠 Config par onglet (label, requêtes, routes…)
//     const TAB_CONFIG = {
//         [TABS.ORDER]: {
//             label: 'Bon de commande',
//             placeholder: 'Recherche bon de commande',
//             list: listAllOrdersCoupons,
//             delete: deleteOrderCoupon,
//             worksiteKey: 'worksite',
//             route: (id) => `/pdf-preview/order/${id}`,
//             enrich: async (items) =>
//                 Promise.all(
//                     items.map(async (el) => {
//                         if (!el?.collaborator && el?.orderCouponCollaboratorId) {
//                             const user = await getUser(el.orderCouponCollaboratorId);
//                             return {
//                                 ...el,
//                                 collaborator: {
//                                     first_name: user?.first_name || '',
//                                     last_name: user?.last_name || '',
//                                 },
//                             };
//                         }
//                         return el;
//                     })
//                 ),
//         },
//         [TABS.INTERVENTION]: {
//             label: "Bon d'intervention",
//             placeholder: 'Recherche bon d’intervention',
//             list: listAllInterventionCoupons,
//             delete: deleteInterventionCoupon,
//             worksiteKey: 'worksite',
//             route: (id) => `/pdf-preview/intervention/${id}`,
//         },
//         [TABS.PREFILL]: {
//             label: 'Bon pré-rempli',
//             placeholder: 'Recherche bon pré-rempli',
//             list: listAllPrefillCoupons,
//             delete: deletePrefillCoupon,
//             worksiteKey: 'worksites',
//             route: (id) => `/pdf-preview/Pre-intervention/${id}`,
//         },
//     };

//     // 🔥 Modal de suppression
//     const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
//     const [couponToDelete, setCouponToDelete] = useState(null);

//     // 🌱 États
//     const [searchParams, setSearchParams] = useSearchParams();

//     const getValidTab = (value) =>
//         Object.values(TABS).includes(value) ? value : TABS.ORDER;

//     const [activeTab, setActiveTab] = useState(() =>
//         getValidTab(searchParams.get('tab'))
//     );
//     const [search, setSearch] = useState('');
//     const [stateByTab, setStateByTab] = useState({
//         [TABS.ORDER]: { ...INITIAL_TAB_STATE },
//         [TABS.INTERVENTION]: { ...INITIAL_TAB_STATE },
//         [TABS.PREFILL]: { ...INITIAL_TAB_STATE },
//     });

//     const currentConfig = TAB_CONFIG[activeTab];
//     const currentState = stateByTab[activeTab];

//     // ✅ Revision maps (ORDER + INTERVENTION uniquement)
//     // - Ancien bon: coupon.revisionOfId = id du nouveau bon
//     // - Nouveau bon: son id est référencé par revisionOfId d’un ancien bon
//     const revisionMaps = useMemo(() => {
//         const enabled =
//             activeTab === TABS.ORDER || activeTab === TABS.INTERVENTION;

//         if (!enabled) {
//             return { enabled: false, newById: new Map(), oldByNewId: new Map() };
//         }

//         const items = currentState?.items || [];

//         const newById = new Map();
//         for (const c of items) {
//             if (c?.id) newById.set(c.id, c);
//         }

//         const oldByNewId = new Map();
//         for (const oldCoupon of items) {
//             const newId = oldCoupon?.revisionOfId;
//             if (newId) oldByNewId.set(newId, oldCoupon);
//         }

//         return { enabled: true, newById, oldByNewId };
//     }, [activeTab, currentState?.items]);


//     const updateCurrentTabState = (patch) => {
//         setStateByTab((prev) => ({
//             ...prev,
//             [activeTab]: {
//                 ...prev[activeTab],
//                 ...(typeof patch === 'function' ? patch(prev[activeTab]) : patch),
//             },
//         }));
//     };

//     const openDeleteModal = (coupon) => {
//         setCouponToDelete(coupon);
//         setIsDeleteModalOpen(true);
//     };

//     const closeDeleteModal = () => {
//         setIsDeleteModalOpen(false);
//         setCouponToDelete(null);
//     };

//     // 📥 Chargement initial de l'onglet sélectionné
//     useEffect(() => {
//         if (!currentState.initialized) {
//             handleLoadMore();
//         }
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [activeTab]);

//     // 🔄 Synchronisation de l’onglet actif avec l’URL
//     useEffect(() => {
//         const urlTab = searchParams.get('tab');
//         const validTab = getValidTab(urlTab);
//         if (validTab !== activeTab) {
//             setActiveTab(validTab);
//         }
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [searchParams]);

//     // 📥 Fonction générique de chargement (pagination)
//     const handleLoadMore = async () => {
//         if (currentState.loading) return;

//         const limit = 25;
//         const { list, enrich } = currentConfig;

//         updateCurrentTabState({ loading: true });

//         try {
//             const res = await list({
//                 limit,
//                 nextToken: currentState.nextToken,
//             });

//             let items = res.items || [];
//             if (enrich) {
//                 items = await enrich(items);
//             }

//             updateCurrentTabState((prev) => ({
//                 items: [...prev.items, ...items],
//                 nextToken: res.nextToken,
//                 loading: false,
//                 initialized: true,
//             }));
//         } catch (err) {
//             console.error(err);
//             toast.error(
//                 '❗️ Une erreur est survenue lors du chargement de la liste des bons'
//             );
//             updateCurrentTabState({ loading: false, initialized: true });
//         }
//     };

//     // 🗑️ Suppression d’un bon (confirmée depuis le modal)
//     const confirmDeleteCoupon = async () => {
//         if (!couponToDelete) return;

//         try {
//             await currentConfig.delete(couponToDelete.id);

//             updateCurrentTabState((prev) => ({
//                 items: prev.items.filter((item) => item.id !== couponToDelete.id),
//             }));
//             toast.success('✅ Bon supprimé avec succès');
//         } catch (err) {
//             console.error(err);
//             toast.error('❗️ Erreur lors de la suppression du bon');
//         } finally {
//             closeDeleteModal();
//         }
//     };

//     // 👉 Détermine si le bon est "à classer"
//     const needsClassification = (coupon) => {
//         const worksite = coupon[currentConfig.worksiteKey] || {};
//         const hasCompany = !!worksite?.company?.name;
//         const hasWorksiteName = !!worksite?.name;

//         // tu peux ajuster la condition selon ton vrai modèle
//         return !(hasCompany && hasWorksiteName);
//     };

//     // 🔍 Filtre de recherche
//     const filteredItems = useMemo(() => {
//         const { items } = currentState;
//         if (!search.trim()) return items;

//         const lower = search.toLowerCase();

//         return items.filter((el) => {
//             const date = el.createdAt || el.dateSigned;
//             const formatted = date ? dateFormat(date) : null;

//             const worksite = el.worksite || el.worksites || {};
//             const fieldsToCheck = [
//                 worksite?.company?.name,
//                 worksite?.name,
//                 worksite?.type?.name,
//                 el?.numero,
//                 el?.collaborator?.first_name,
//                 el?.collaborator?.last_name,
//             ]
//                 .filter(Boolean)
//                 .map((v) => String(v).toLowerCase());

//             if (formatted) {
//                 fieldsToCheck.push(
//                     formatted.day,
//                     formatted.month,
//                     formatted.year
//                 );
//             }

//             return fieldsToCheck.some((field) => field.includes(lower));
//         });
//     }, [search, currentState]);

//     // 📄 Navigation vers le PDF
//     const handleRowClick = (coupon) => {
//         if (coupon.ctm === 'true') return;
//         navigate(currentConfig.route(coupon.id));
//     };

//     const getCouponTypeLabel = () => currentConfig.label;

//     return (
//         <section className="md:pt-8 px-4 md:px-9">
//             {/* Onglets */}
//             <div className="flex gap-3 mt-4 border-b border-gray-200">
//                 {Object.values(TABS).map((tabKey) => {
//                     const cfg = TAB_CONFIG[tabKey];
//                     const isActive = activeTab === tabKey;

//                     return (
//                         <button
//                             key={tabKey}
//                             className={`pb-2 px-3 text-sm md:text-base ${isActive
//                                 ? 'border-b-2 border-primary-blue font-semibold text-primary-blue'
//                                 : 'text-gray-500'
//                                 }`}
//                             onClick={() => {
//                                 setActiveTab(tabKey);
//                                 setSearchParams({ tab: tabKey });
//                             }}
//                         >
//                             {cfg.label}s
//                         </button>
//                     );
//                 })}
//             </div>

//             {/* Search bar */}
//             <div className="relative flex mt-6 border border-gray-admin rounded-xl">
//                 <input
//                     type="text"
//                     placeholder={currentConfig.placeholder}
//                     className="w-full py-3 pl-10 border-none rounded-xl placeholder:text-blue-admin inputFocus"
//                     value={search}
//                     onChange={(e) => setSearch(e.target.value)}
//                 />

//                 <img
//                     src={Search}
//                     alt="search icon"
//                     className="absolute left-0 flex items-center w-8 h-5 pl-3 top-4"
//                 />
//             </div>

//             {/* Table */}
//             <div className="mt-9">
//                 <Table>
//                     <thead>
//                         <tr>
//                             {HEADERS.map((header, index) => {
//                                 const label = typeof header === 'string' ? header : header.label;
//                                 const className = typeof header === 'string' ? '' : header.className;

//                                 return (
//                                     <th key={index} className={className}>
//                                         {label}
//                                     </th>
//                                 );
//                             })}
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {filteredItems?.length ? (
//                             filteredItems
//                                 .slice()
//                                 .sort((a, b) => (b.numero || 0) - (a.numero || 0))
//                                 .map((coupon) => {
//                                     const dateValue = coupon.createdAt || coupon.dateSigned;
//                                     const date = dateValue ? dateFormat(dateValue) : null;

//                                     const worksite = coupon[currentConfig.worksiteKey] || {};
//                                     const typeName = worksite?.type?.name || '-';
//                                     const companyName = worksite?.company?.name || coupon?.nameClient || '-';
//                                     const worksiteName = worksite?.name || coupon?.nameWorksite || '-';

//                                     const fullName = coupon?.collaborator
//                                         ? `${coupon.collaborator.first_name || ''} ${coupon.collaborator.last_name || ''
//                                             }`.trim()
//                                         : '';
//                                     const createdBy = coupon?.name;

//                                     const toClassify = needsClassification(coupon);

//                                     // 🔁 Revision info (uniquement ORDER / INTERVENTION)
//                                     let revisionInfo = null;

//                                     if (revisionMaps.enabled) {
//                                         // Cas 1: ce bon est l'ancien (il a revisionOfId)
//                                         if (coupon?.revisionOfId) {
//                                             const newCoupon = revisionMaps.newById.get(coupon.revisionOfId) || null;
//                                             revisionInfo = { kind: 'OLD', newCoupon };
//                                         } else {
//                                             // Cas 2: ce bon est le nouveau (un ancien pointe vers lui)
//                                             const oldCoupon = revisionMaps.oldByNewId.get(coupon.id) || null;
//                                             if (oldCoupon) revisionInfo = { kind: 'NEW', oldCoupon };
//                                         }
//                                     }


//                                     return (
//                                         <tr
//                                             key={coupon.id}
//                                             className={`h-16 border-b border-white-admin hover:bg-blue-50 transition-all ${coupon.ctm !== 'true' ? 'cursor-pointer' : ''
//                                                 }`}
//                                             onClick={() => handleRowClick(coupon)}
//                                         >
//                                             {/* <td>{getCouponTypeLabel()}</td> */}
//                                             <td>
//                                                 {date
//                                                     ? `${date.day}.${date.month}.${date.year}`
//                                                     : '-'}
//                                             </td>
//                                             <td>{fullName || createdBy || '-'}</td>
//                                             <td onClick={(e) => e.stopPropagation()}>
//                                                 <div className="flex flex-col gap-1">
//                                                     <div>
//                                                         <span className="font-medium">
//                                                             {coupon?.numero} {coupon.ctm === 'true' ? '- pré-rempli' : ''}
//                                                         </span>
//                                                     </div>

//                                                     {/* ✅ Affichage revision (ORDER + INTERVENTION seulement) */}
//                                                     {revisionInfo?.kind === 'OLD' && (
//                                                         <div className="flex items-center gap-2">
//                                                             <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-orange-100 text-orange-700">
//                                                                 Modifié
//                                                             </span>

//                                                             <span className="text-xs text-gray-600">
//                                                                 → remplacé par{' '}
//                                                                 <span className="font-semibold">
//                                                                     {revisionInfo.newCoupon?.numero
//                                                                         ? `N° ${revisionInfo.newCoupon.numero}`
//                                                                         : 'un nouveau bon'}
//                                                                 </span>
//                                                             </span>

//                                                             {revisionInfo.newCoupon?.id && (
//                                                                 <button
//                                                                     type="button"
//                                                                     className="text-xs text-blue-admin underline"
//                                                                     onClick={() => navigate(currentConfig.route(revisionInfo.newCoupon.id))}
//                                                                 >
//                                                                     Voir le nouveau
//                                                                 </button>
//                                                             )}
//                                                         </div>
//                                                     )}

//                                                     {revisionInfo?.kind === 'NEW' && (
//                                                         <div className="flex items-center gap-2">
//                                                             <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-700">
//                                                                 Révision
//                                                             </span>

//                                                             <span className="text-xs text-gray-600">
//                                                                 remplace{' '}
//                                                                 <span className="font-semibold">
//                                                                     {revisionInfo.oldCoupon?.numero
//                                                                         ? `N° ${revisionInfo.oldCoupon.numero}`
//                                                                         : 'un ancien bon'}
//                                                                 </span>
//                                                             </span>
//                                                         </div>
//                                                     )}
//                                                 </div>
//                                             </td>
//                                             <td>{typeName || '-'}</td>
//                                             <td>{companyName || '-'}</td>
//                                             <td>{worksiteName || '-'}</td>
//                                             {/* ✅ Statut / À classer */}
//                                             <td onClick={(e) => e.stopPropagation()}>
//                                                 {toClassify ? (
//                                                     <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
//                                                         À classer
//                                                     </span>
//                                                 ) : (
//                                                     <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-50 text-green-600">
//                                                         Classé
//                                                     </span>
//                                                 )}
//                                             </td>
//                                             <td
//                                                 className="text-right"
//                                                 onClick={(e) => e.stopPropagation()}
//                                             >
//                                                 <div>
//                                                     <button
//                                                         onClick={() => openDeleteModal(coupon)}
//                                                         className="w-8 p-2 border rounded-lg mt-7 border-white-admin"
//                                                         type="button"
//                                                     >
//                                                         <img src={DeleteIcon} alt="delete icon" />
//                                                     </button>
//                                                 </div>
//                                             </td>
//                                         </tr>
//                                     );
//                                 })
//                         ) : (
//                             <tr>
//                                 <td
//                                     className="text-center text-lg font-semibold pt-20"
//                                     colSpan={8} // ✅ 1 colonne de plus (Statut)
//                                 >
//                                     {search
//                                         ? "Oups, aucun bon ne correspond à votre recherche 🤭"
//                                         : `Aucun ${getCouponTypeLabel().toLowerCase()} pour le moment`}
//                                 </td>
//                             </tr>
//                         )}
//                     </tbody>
//                 </Table>

//                 {/* Pagination */}
//                 {currentState.initialized &&
//                     (currentState.loading || currentState.nextToken) && (
//                         <div className="flex justify-center my-6">
//                             <button
//                                 disabled={currentState.loading || !currentState.nextToken}
//                                 onClick={handleLoadMore}
//                                 className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
//                             >
//                                 {currentState.loading
//                                     ? 'Chargement...'
//                                     : currentState.nextToken
//                                         ? 'Charger plus'
//                                         : 'Plus de résultats'}
//                             </button>
//                         </div>
//                     )}
//             </div>

//             {/* Modal suppression */}
//             <ReactModal
//                 isOpen={isDeleteModalOpen}
//                 onRequestClose={closeDeleteModal}
//                 className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto outline-none mt-80"
//                 overlayClassName="fixed inset-0 bg-black/40 flex items-start justify-center z-50"
//             >
//                 <h2 className="text-lg font-semibold mb-2">
//                     Confirmer la suppression
//                 </h2>

//                 <p className="text-sm text-gray-700 mb-6">
//                     Voulez-vous vraiment supprimer le bon n°{' '}
//                     <span className="font-semibold">
//                         {couponToDelete?.numero || couponToDelete?.id}
//                     </span>
//                     {' '}?
//                 </p>

//                 <div className="flex justify-end gap-3">
//                     <button
//                         type="button"
//                         onClick={closeDeleteModal}
//                         className="px-4 py-2 rounded-lg border text-sm"
//                     >
//                         Annuler
//                     </button>
//                     <button
//                         type="button"
//                         onClick={confirmDeleteCoupon}
//                         className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700"
//                     >
//                         Supprimer
//                     </button>
//                 </div>
//             </ReactModal>
//         </section>
//     );
// };

// export default Coupons;
